import { navigate } from 'gatsby-link';
import { v4 as uuidv4 } from 'uuid';

import {
  STATIC_WIZARD_PARAMS,
  WIZARD_ID,
  WIZARD_NAME,
} from '../../../shared/constants';

import { DEQ_PAGE_TYPES, formTrackPageView } from '../../../utils/deq';
import { FORM_TRACKING_STATUS } from '../../../utils/deq/formTrackPageView';

import { WizardState } from '../../../state/wizard/Wizard.store.types';
import {
  LocalStorage,
  WizardOptionCondition,
  WizardResultState,
} from '../../../tokens';
import { WizardItem } from '../../../types/wizard';

import { parseTagsToQueryParameter } from './WizardLayout.helpers';
import {
  QueryParameterState,
  WizardElementStates,
  WizardMetaData,
} from './WizardLayout.types';

const stateParameterID = 'state';
const tagsParameterID = 'tags';
const queryResultState = 'results';

const getCorrectWizardID = (name: string, id: string) => {
  switch (name) {
    case WIZARD_NAME.cyberWizard:
      return WIZARD_ID.oldCyberWizard;
    case WIZARD_NAME.intakeWizard:
      return WIZARD_ID.oldIntakeWizard;
    default:
      return id;
  }
};

const getTrackingFormContext = (
  stepIndex: number,
  step?: WizardItem
): string => {
  if (!step) return '';

  const stepSelectionIds = step.items.reduce(
    (accumulator: string, option: WizardItem): string => {
      const foundItems = option.items.filter((item) => item.value);

      return `${accumulator}${foundItems
        .map((item) => item.id.slice(0, 5))
        .join('')}`;
    },
    ''
  );

  const stepName = `${step.tracking.mainStep + stepIndex}${
    step.tracking.subStep
  }`;

  if (!stepSelectionIds.length) {
    return `${stepName}-no-selection`;
  }

  return `${stepName}-${stepSelectionIds}`;
};

let previousStepIndex = -1;

const trackWizard = (
  steps: WizardState['steps'],
  stepIndex: number,
  elementStates: WizardElementStates,
  optionConditions: WizardOptionCondition[],
  resultState: WizardResultState,
  metaData: WizardMetaData
) => {
  // Reset previousStepIndex when navigating back in step index.
  if (stepIndex <= previousStepIndex) {
    previousStepIndex = -1;
  }

  const { intro } = elementStates;
  const previousStep =
    previousStepIndex > -1 ? steps[previousStepIndex] : undefined;
  const step = steps[stepIndex];
  const lastStep = steps[steps.length - 1];
  const baseStepIndex = elementStates.intro.enabled ? 2 : 1;
  const isEligible = !optionConditions.includes(
    WizardOptionCondition.SKIP_STEPS
  );

  let stepId = '';
  let stepName = '';
  let formContext = '';
  let formStatus = '';
  let formOutcome;

  // Intro
  if (intro.active) {
    stepId = '10';
    stepName = 'intro';
  }

  // Steps
  if (step && !intro.active && stepIndex < steps.length) {
    stepId = `${step.tracking.mainStep + baseStepIndex}${
      step.tracking.subStep
    }`;
    stepName = step.tracking.name;
    previousStepIndex = stepIndex;
  }

  // Form active
  if (lastStep && elementStates.form.active && stepIndex === steps.length) {
    stepId = `${lastStep.tracking.mainStep + baseStepIndex + 1}0`;
    stepName = elementStates.form.tracking?.name || '';
  }

  // Set tracking context
  formContext = getTrackingFormContext(baseStepIndex, previousStep);

  // Form success & end steps
  if (
    isEligible &&
    lastStep &&
    stepIndex === steps.length &&
    !elementStates.form.active
  ) {
    if (elementStates.form.enabled) {
      stepId = `${lastStep.tracking.mainStep + baseStepIndex + 2}0`;
      formContext = '';
    } else {
      stepId = `${lastStep.tracking.mainStep + baseStepIndex + 1}0`;
    }

    stepName = 'end';
    formStatus = FORM_TRACKING_STATUS.endSuccess;

    if (metaData.name === WIZARD_NAME.youlend) {
      const tpciID = localStorage.getItem(LocalStorage.TPCI_ID) || uuidv4();
      formOutcome = tpciID;

      localStorage.setItem(LocalStorage.TPCI_ID, tpciID);
    }
  }

  // Values in case the active step has a condition to skip all steps.
  if (!isEligible) {
    stepId = '100';
    stepName = 'not eligible';
    formStatus = FORM_TRACKING_STATUS.endFail;
  }

  // In case the wizard has no results at the end of the wizard.
  if (resultState === WizardResultState.EMPTY) {
    stepId = '100';
    stepName = 'no results';
    formStatus = FORM_TRACKING_STATUS.endFail;
  }

  formTrackPageView(
    DEQ_PAGE_TYPES.action,
    getCorrectWizardID(metaData.name, metaData.id),
    metaData.trackingName,
    stepName.toLowerCase(),
    stepId,
    metaData.version,
    metaData.leadType,
    formContext,
    metaData.eventName,
    formStatus,
    formOutcome
  );
};

const confirmWizardParam = (wizardParam: string) => {
  if (typeof window === 'undefined') return;

  if (window.location.search.indexOf(wizardParam) !== -1) {
    return true;
  }
  return false;
};

const getQueryParameterState = (): QueryParameterState => {
  const { location } = window;
  const locationSearch = new URLSearchParams(location.search);
  const stateParameter = locationSearch.get(stateParameterID);
  const tagsParameter = locationSearch.get(tagsParameterID);

  return {
    state: stateParameter || '',
    tags: tagsParameter || '',
  };
};

// Util function to check if the correct state and tags (results) are in the query parameter.
const hasQueryParameterResults = (): boolean => {
  const queryParameterState = getQueryParameterState();

  return (
    queryParameterState.state === queryResultState && !!queryParameterState.tags
  );
};

const updateUrl = (stepIndex: number, elementStates: WizardElementStates) => {
  const { location } = window;
  let stepState = 'step';

  if (elementStates.intro.active) {
    stepState = 'intro';
  }

  if (elementStates.form.active) {
    stepState = 'form';
  }

  if (elementStates.result?.enabled) {
    stepState = `${elementStates.result?.active ? 'results' : 'no-results'}`;
  }

  const userFromParam = `&userfrom=${STATIC_WIZARD_PARAMS.openDomain}`;
  const currentParam = location.search.includes(userFromParam)
    ? userFromParam
    : '';
  const tagParam = elementStates.result?.enabled
    ? parseTagsToQueryParameter(elementStates.result?.tags || [])
    : '';

  const path = `${location.pathname}?state=${stepState}&index=${stepIndex}${tagParam}${currentParam}`;

  if (path !== `${location.pathname}${location.search}`) {
    navigate(path);
  }
};

export {
  confirmWizardParam,
  getQueryParameterState,
  hasQueryParameterResults,
  trackWizard,
  updateUrl,
};
